import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, Heart, Shield, Truck } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "About Us", item: `${SITE}/about` },
  ],
};

export default function About() {
  return (
    <>
      <PageSEO
        title="About Enchanted Gear Rentals | Formerly Enchanted Baby Gear Rentals"
        description="Enchanted Gear Rentals, formerly Enchanted Baby Gear Rentals, is the same family-owned Orlando-area stroller and baby-gear rental company under an expanded new name."
        canonical={`${SITE}/about`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">About Us</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">About Enchanted Gear Rentals</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              A family-owned Orlando-area rental company dedicated to making your Walt Disney World and Florida vacation easier from the moment you land.
            </p>
          </div>
        </section>

        <div className="container max-w-3xl py-16 space-y-16">
          {/* Business name transition — prominent, clear */}
          <section className="rounded-2xl bg-[#0A1825] border-l-4 border-[#FFD166] pl-6 pr-6 py-6">
            <h2 className="font-serif text-2xl text-[#FFD166] mb-3">Formerly Enchanted Baby Gear Rentals</h2>
            <p className="text-[#B9C8D5] leading-relaxed">
              Enchanted Gear Rentals, formerly Enchanted Baby Gear Rentals, is the same family-owned Orlando-area rental company under an expanded new name. Our inventory, service and years of rental experience carried forward with us.
            </p>
            <p className="text-[#B9C8D5] leading-relaxed mt-4">
              As our inventory grew beyond traditional baby gear to include premium strollers, family travel equipment and mobility rentals, our name expanded with us. <strong className="text-[#F6F1E4]">We are not two separate companies.</strong> We are one family-owned business that has been serving Orlando families for years — now doing it under a name that more accurately describes what we provide.
            </p>
            <p className="text-[#B9C8D5] leading-relaxed mt-4">
              Customers who previously used Enchanted Baby Gear Rentals will find the same family, the same equipment and the same standards here at enchantedgearrentals.com.
            </p>
          </section>

          {/* Our story */}
          <section>
            <h2 className="font-serif text-3xl text-[#F6F1E4] mb-5">Our story</h2>
            <div className="space-y-4 text-[#B9C8D5] leading-relaxed">
              <p>We are parents who have taken our own kids to Walt Disney World, Universal Orlando and the Florida theme parks. We know what it is like to navigate a resort with a toddler who just hit a wall at 2 pm. We know what it is like to travel light and still need the right gear.</p>
              <p>We started this business because we saw a real need: families were either lugging heavy strollers through airports or paying too much for substandard rentals with complicated drop-off requirements. We wanted to do it differently — premium gear, simple booking, honest policies and people who show up when they say they will.</p>
              <p>Today, Enchanted Gear Rentals serves families at Walt Disney World resorts, Universal Orlando, area hotels, vacation homes and Airbnb and VRBO properties throughout the greater Orlando area.</p>
            </div>
          </section>

          {/* Values */}
          <section>
            <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">What we stand for</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { Icon: Heart, title: "Family first", body: "Every rental decision is made the way we would want a rental company to treat our own family. Clear policies. Honest communication. No surprises." },
                { Icon: Shield, title: "Clean gear you can trust", body: "Every stroller and piece of equipment is sanitized between rentals using hospital-grade disinfectants and inspected before it reaches your family." },
                { Icon: Truck, title: "We come to you", body: "Delivery is free. We meet you in person at your resort, hotel or vacation home. We do not leave equipment unattended." },
              ].map(({ Icon, title, body }) => (
                <div key={title} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-5">
                  <Icon className="w-6 h-6 text-[#FFD166] mb-3" />
                  <h3 className="font-serif text-lg text-[#F6F1E4] mb-2">{title}</h3>
                  <p className="text-sm text-[#B9C8D5] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Disclaimer */}
          <section className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
            <p className="text-xs text-[#B9C8D5]">
              Enchanted Gear Rentals is an independent family-owned business. We are not affiliated with, authorized by, or endorsed by The Walt Disney Company, Universal Parks &amp; Resorts, or any other theme park operator. Walt Disney World®, Universal Studios®, and related marks are trademarks of their respective owners.
            </p>
          </section>

          {/* CTA */}
          <section className="text-center">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Ready to book your rental?</h2>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="about-book-cta">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/contact"><button className="btn-ghost">Contact us</button></Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
